import { BugReportOutlined, PlayArrowOutlined } from "@mui/icons-material";
import {
    ListItem,
    ListItemButton,
    ListItemIcon,
    Typography,
    Box,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button
} from "@mui/material";
import SandboxDefinition from "../../../types/SandboxDefinition";
import { SandboxResult } from "../../../types/SandboxResult";
import { LoadingButton } from "@mui/lab";
import { MouseEvent, useState } from "react";

interface SandboxListItemProps {
    item: SandboxDefinition;
    isSelected: boolean;
    onSelect: () => void;

    onRun: () => void;
    isLoading: boolean;
    result: SandboxResult | null;

    onItemDoubleClicked?: () => void;
    onItemDelete?: () => void;
}

export default function SandboxListItem(props: SandboxListItemProps) {
    // Determine button color based on last result
    let buttonColor: "primary" | "success" | "error" = "primary";

    if (props.result !== null) {
        buttonColor = props.result.isSuccessful ? "success" : "error";
    }

    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const onDoubleClick = () => {
        props.onItemDoubleClicked && props.onItemDoubleClicked();
    };

    const handleContextMenu = (event: MouseEvent<HTMLDivElement | HTMLLIElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setMenuAnchor(event.currentTarget as HTMLElement);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleDeleteMenuClick = () => {
        handleMenuClose();
        setConfirmOpen(true);
    };

    const handleConfirmClose = () => {
        setConfirmOpen(false);
    };

    const handleConfirmDelete = () => {
        setConfirmOpen(false);
        if (props.onItemDelete) {
            props.onItemDelete();
        }
    };

    return (
        <>
            <ListItem
                disablePadding
                onDoubleClick={onDoubleClick}
                onContextMenu={handleContextMenu}
            >
                <ListItemButton
                    selected={props.isSelected}
                    onClick={() => props.onSelect()}
                >
                    <ListItemIcon sx={{ minWidth: 30, mr: 0.5 }}>
                        <BugReportOutlined fontSize="small" sx={{ color: "text.secondary" }} />
                    </ListItemIcon>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            gap: 1
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 13,
                                flexGrow: 1,
                                whiteSpace: "normal",
                                wordBreak: "break-word"
                            }}
                            component="span"
                            color="text.primary"
                        >
                            {props.item.name}
                        </Typography>

                        <LoadingButton
                            loading={props.isLoading}
                            size="small"
                            variant="contained"
                            disabled={props.isLoading}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation(); // don't trigger row selection
                                props.onRun();
                            }}
                            sx={{ minWidth: 36, px: 1 }}
                            color={buttonColor}
                        >
                            <PlayArrowOutlined fontSize="small" />
                        </LoadingButton>
                    </Box>
                </ListItemButton>
            </ListItem>

            {/* Right-click context menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MenuItem onClick={handleDeleteMenuClick}>
                    Delete
                </MenuItem>
            </Menu>

            {/* Delete confirmation dialog */}
            <Dialog
                open={confirmOpen}
                onClose={handleConfirmClose}
            >
                <DialogTitle>Delete sandbox</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the sandbox &quot;{props.item.name}&quot;?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
